import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { DefaultTheme } from '../theme';
import { applyOptionOrdering, findVariantByOptions } from './optionHelper';
import { ValidationMessage } from '../form/FormRow';

import Swatch from './swatch';
import Dropdown from './dropdown';

function ProductOptionsSelector({
  productData: product,
  activeVariant,
  optionChangeHandler,
  hasSizeChart,
  measureIconColor,
  showOptionErrorMessages,
  theme,
}) {
  const { tt } = useUiComponentsInternationalContext();
  // ! default to first variant if no active to set option values
  const activeSelectedOptions =
    activeVariant?.selectedOptions || product.variants[0]?.selectedOptions;

  // only has color as option
  const onlyColorOption =
    product.options.length === 1 &&
    product.options.every(o => o.name.toLowerCase() === 'color');

  // on first load if not activeVariant we show everything as availableForSale until they click and is not a color only product
  const overrideAvailability = !onlyColorOption && !activeVariant;

  const orderedOptions = applyOptionOrdering(product?.options, [
    'size',
    'color',
  ]);

  return (
    <div className="product-options">
      {orderedOptions
        ?.filter(opt => opt.name.toLowerCase() !== 'title')
        ?.map(opt => (
          <React.Fragment key={opt.name}>
            {opt.name.toLowerCase() === 'color' ? (
              <Swatch
                key={opt.name}
                name={tt(s => s.productCommon.colorPickerLabel)}
                values={opt.values}
                overrideAvailability={overrideAvailability}
                activeValue={activeVariant?.sku}
                activeSelectedOptions={activeSelectedOptions}
                findVariantByOptions={findVariantByOptions}
                variants={product.variants}
                optionChangeHandler={optionChangeHandler}
                borderColor={theme.colors.inputBorder}
              />
            ) : (
              <Dropdown
                key={opt.name}
                name={opt.name}
                values={opt.values}
                activeValue={activeVariant?.sku}
                activeSelectedOptions={activeSelectedOptions}
                findVariantByOptions={findVariantByOptions}
                variants={product.variants}
                optionChangeHandler={optionChangeHandler}
                hasSizeChart={
                  opt.name.toLowerCase() === 'size' ? hasSizeChart : false
                }
                measureIconColor={measureIconColor}
              />
            )}
            <ValidationMessage>
              {showOptionErrorMessages &&
                tt(
                  s => s.productCommon.chooseAvailableOptionValidationMessage,
                  {
                    option: opt.name.toLowerCase(),
                  }
                )}
            </ValidationMessage>
          </React.Fragment>
        ))}
    </div>
  );
}

ProductOptionsSelector.propTypes = {
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      inputBorder: PropTypes.string,
    }),
  }),
  productData: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        selectedOptions: PropTypes.arrayOf(
          PropTypes.shape({ name: PropTypes.string, value: PropTypes.string })
        ),
      })
    ),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
      }).isRequired
    ),
  }),
  activeVariant: PropTypes.shape({
    selectedOptions: PropTypes.arrayOf(
      PropTypes.shape({ name: PropTypes.string, value: PropTypes.string })
    ),
    sku: PropTypes.string.isRequired,
  }),
  optionChangeHandler: PropTypes.func,
  hasSizeChart: PropTypes.bool,
  measureIconColor: PropTypes.string,
  showOptionErrorMessages: PropTypes.bool.isRequired,
};

ProductOptionsSelector.defaultProps = {
  theme: DefaultTheme,
  productData: {
    variants: [],
    options: [],
  },
  activeVariant: null,
  optionChangeHandler: null,
  hasSizeChart: false,
  measureIconColor: '#000',
};

export default withTheme(ProductOptionsSelector);
